/**
 * Selection UI interaction sink: turns coarse UI events (indices, phases) into
 * metrics and product analytics with stable ids and DOM context.
 *
 * **Stabilized metrics:** Uses one `MetricRef` per metric kind. Every {@link SelectionUiInteractionSinkHandle.emit}
 * first **silently** cancels **all six** in-flight refs (last-write-wins across cell selection,
 * cell range selection pointer-drag commit, inline-edit, row checkbox, header checkbox, and cell
 * context menu), then
 * `fireMetric` runs for this event. Supersede and lifecycle cancels do **not** publish
 * `failureWithDuration`. Bootstrap
 * wires `visibilitychange` / `pagehide` to {@link SelectionUiInteractionSinkHandle.cancelInFlightMetrics};
 * `dispose` cancels the same six refs on teardown.
 *
 * **Inline edit:** `editRequested` uses `failImmediately` with `cell-inline-edit-not-permitted`
 * when the user cannot edit content or the cell does not support an inline-edit transition
 * (`canEditDatabaseContent` / `canRequestValueEdit`), matching reducer gating so probes are
 * not started for impossible edits (e.g. always-edit surfaces like checkboxes).
 */
import {
  analyticsClient,
  getDatabaseContainerType,
  getSelectionsAnalyticsAttributes,
} from '@databases/analytics';
import {
  cancelMetric,
  createMetricRef,
  fireMetric,
} from '@databases/frontend-metrics/fire-metric';
import { cellContextMenuMetric } from '@databases/frontend-metrics/metrics/cell-context-menu';
import { cellInlineEditMetric } from '@databases/frontend-metrics/metrics/cell-inline-edit';
import { cellRangeSelectionPointerDragCommitMetric } from '@databases/frontend-metrics/metrics/cell-range-selection-pointer-drag-commit';
import { cellSelectionMetric } from '@databases/frontend-metrics/metrics/cell-selection';
import { entryCheckboxMetric } from '@databases/frontend-metrics/metrics/entry-checkbox';
import { headerCheckboxMetric } from '@databases/frontend-metrics/metrics/header-checkbox';
import { type SelectionUiInteraction } from '@databases/selections';
import { type DatabaseAdapter } from '@databases/selections-api-types';

const LAYOUT_TABLE_SELECTOR = '[data-testid="layout-table"]';

/**
 * Handle returned by {@link createSelectionUiInteractionSink}. Call `dispose` when tearing
 * down the feature (permissions/view rebuild or database teardown).
 */
export type SelectionUiInteractionSinkParams = {
  /** Database content id attached to metric metadata and analytics payloads. */
  contentId: string;

  /**
   * Maps visible grid indices to ids for the current view; avoids copying full id lists
   * per emit.
   */
  databaseAdapter: DatabaseAdapter;

  /**
   * Must mirror selection reducer policy (e.g. `permissionsManager.getPermissions().canEditContent`)
   * so inline-edit metrics are not started when the reducer would refuse `startEditing`.
   */
  canEditDatabaseContent: () => boolean;
};

export type SelectionUiInteractionSinkHandle = {
  emit(event: SelectionUiInteraction): void;
  /** Clears cached layout table and silently cancels all six metric refs. */
  dispose(): void;
  /**
   * Silently cancels in-flight selection UI metrics only (six refs). Used for document
   * lifecycle; does not cancel other stabilized metrics in the app. Does not publish Reliability.
   */
  cancelInFlightMetrics: () => void;
};

/**
 * Factory for the databases selection UI metrics path. The table emits
 * {@link SelectionUiInteraction} with row/column indices; this sink resolves them to
 * `entryId` / `fieldId` via `databaseAdapter` (view-aware, current snapshot per call)
 * and records frontend metrics. Header checkbox selection also sends an analytics
 * event on a microtask so the hot path stays light.
 *
 * Metrics fire synchronously from `emit` except that header checkbox product
 * analytics is scheduled with `queueMicrotask`. If indices are out of range or
 * the layout table root is missing, metrics may no-op or fail fast per metric
 * configuration.
 *
 * Caches the layout table element (`[data-testid="layout-table"]`) after first
 * resolve; call `dispose` when tearing down the feature so the next mount can
 * re-query the DOM.
 *
 * @param params - See {@link SelectionUiInteractionSinkParams}.
 */
export function createSelectionUiInteractionSink(
  params: SelectionUiInteractionSinkParams,
): SelectionUiInteractionSinkHandle {
  const { contentId, databaseAdapter, canEditDatabaseContent } = params;

  let cachedLayoutTable: HTMLElement | null | undefined = undefined;

  const cellSelectionMetricRef = createMetricRef();
  const cellRangeSelectionPointerDragCommitMetricRef = createMetricRef();
  const cellInlineEditMetricRef = createMetricRef();
  const entryCheckboxMetricRef = createMetricRef();
  const headerCheckboxMetricRef = createMetricRef();
  const cellContextMenuMetricRef = createMetricRef();

  function cancelAllInFlight() {
    cancelMetric(cellSelectionMetricRef);
    cancelMetric(cellRangeSelectionPointerDragCommitMetricRef);
    cancelMetric(cellInlineEditMetricRef);
    cancelMetric(entryCheckboxMetricRef);
    cancelMetric(headerCheckboxMetricRef);
    cancelMetric(cellContextMenuMetricRef);
  }

  function getTableRoot(): HTMLElement | null {
    if (cachedLayoutTable !== undefined && cachedLayoutTable !== null) {
      return cachedLayoutTable;
    }

    const el = document.querySelector<HTMLElement>(LAYOUT_TABLE_SELECTOR);

    if (el) {
      cachedLayoutTable = el;
    }

    return el;
  }

  function emitCellPointer(
    event: Extract<SelectionUiInteraction, { source: 'cellPointer' }>,
  ) {
    const tableRoot = getTableRoot();
    const entryId = databaseAdapter.getVisibleEntryIdAt(event.detail.rowIndex);
    const fieldId = databaseAdapter.getVisibleFieldIdAt(event.detail.colIndex);

    if (!entryId || !fieldId) {
      return;
    }

    switch (event.detail.phase) {
      case 'selectionCommitted':
        fireMetric(
          cellSelectionMetric,
          {
            startTime: event.startTime,
            probeData: {
              tableRoot,
              selectionProbe: 'focusBox',
              entryId,
              fieldId,
            },
            metadata: {
              contentId,
              selectionProbe: 'focusBox',
              inputSource: 'pointer',
            },
            ...(!tableRoot && {
              failImmediately: { reason: 'cell-selection-dom-sync-missing-table-root' },
            }),
          },
          cellSelectionMetricRef,
        );

        return;
      case 'rangeSelectionPointerDragCommitted':
        fireMetric(
          cellRangeSelectionPointerDragCommitMetric,
          {
            startTime: event.startTime,
            probeData: {
              tableRoot,
              rowIndex: event.detail.rowIndex,
              colIndex: event.detail.colIndex,
              entryId,
              fieldId,
            },
            metadata: {
              contentId,
            },
            ...(!tableRoot && {
              failImmediately: {
                reason:
                  'cell-range-selection-pointer-drag-commit-dom-sync-missing-table-root',
              },
            }),
          },
          cellRangeSelectionPointerDragCommitMetricRef,
        );

        return;
      case 'editRequested':
        break;
      default: {
        const unhandledPhase: never = event.detail.phase;

        return unhandledPhase;
      }
    }

    if (!tableRoot) {
      fireMetric(
        cellInlineEditMetric,
        {
          startTime: event.startTime,
          probeData: {
            tableRoot,
            entryId,
            fieldId,
          },
          metadata: {
            contentId,
            inputSource: 'pointer',
          },
          failImmediately: { reason: 'cell-inline-edit-dom-sync-missing-table-root' },
        },
        cellInlineEditMetricRef,
      );

      return;
    }

    if (
      !canEditDatabaseContent() ||
      !databaseAdapter.canRequestValueEdit(fieldId, entryId)
    ) {
      fireMetric(
        cellInlineEditMetric,
        {
          startTime: event.startTime,
          probeData: {
            tableRoot,
            entryId,
            fieldId,
          },
          metadata: {
            contentId,
            inputSource: 'pointer',
          },
          failImmediately: { reason: 'cell-inline-edit-not-permitted' },
        },
        cellInlineEditMetricRef,
      );

      return;
    }

    fireMetric(
      cellInlineEditMetric,
      {
        startTime: event.startTime,
        probeData: {
          tableRoot,
          entryId,
          fieldId,
        },
        metadata: {
          contentId,
          inputSource: 'pointer',
        },
      },
      cellInlineEditMetricRef,
    );
  }

  function emitRowCheckbox(
    event: Extract<SelectionUiInteraction, { source: 'rowCheckbox' }>,
  ) {
    const entryId = databaseAdapter.getVisibleEntryIdAt(event.detail.rowIndex);

    if (!entryId) {
      return;
    }

    const tableRoot = getTableRoot();

    fireMetric(
      entryCheckboxMetric,
      {
        startTime: event.startTime,
        probeData: {
          tableRoot,
          probe: {
            kind: 'single-row',
            entryId,
            expectRowCheckboxChecked: event.detail.expectRowCheckboxChecked,
          },
        },
        metadata: {
          contentId,
          isShiftRangeSelect: event.modifiers.shift,
        },
        ...(!tableRoot && {
          failImmediately: { reason: 'entry-checkbox-dom-sync-missing-table-root' },
        }),
      },
      entryCheckboxMetricRef,
    );

    queueMicrotask(() => {
      void analyticsClient
        .sendRowCheckboxSelectedEvent({
          entryId,
          contentId,
          databaseContainer: getDatabaseContainerType(),
          checkboxState: event.detail.expectRowCheckboxChecked
            ? 'selected'
            : 'unselected',
          ...getSelectionsAnalyticsAttributes(contentId),
        })
        .catch(() => undefined);
    });
  }

  function emitContextMenu(
    event: Extract<SelectionUiInteraction, { source: 'contextMenu' }>,
  ) {
    const entryId = databaseAdapter.getVisibleEntryIdAt(event.detail.rowIndex);
    const fieldId = databaseAdapter.getVisibleFieldIdAt(event.detail.colIndex);

    if (!entryId || !fieldId) {
      return;
    }

    fireMetric(
      cellContextMenuMetric,
      {
        startTime: event.startTime,
        probeData: {
          entryId,
          fieldId,
        },
        metadata: {
          contentId,
        },
      },
      cellContextMenuMetricRef,
    );
  }

  function emitHeaderCheckbox(
    event: Extract<SelectionUiInteraction, { source: 'headerCheckbox' }>,
  ) {
    const tableRoot = getTableRoot();

    fireMetric(
      headerCheckboxMetric,
      {
        startTime: event.startTime,
        probeData: {
          tableRoot,
          expectChecked: event.detail.expectChecked,
        },
        metadata: {
          contentId,
        },
        ...(!tableRoot && {
          failImmediately: { reason: 'header-checkbox-dom-sync-missing-table-root' },
        }),
      },
      headerCheckboxMetricRef,
    );

    queueMicrotask(() => {
      void analyticsClient
        .sendHeaderCheckboxSelectedEvent({
          contentId,
          databaseContainer: getDatabaseContainerType(),
          entriesCount: databaseAdapter.getVisibleEntryCount(),
          checkboxState: event.detail.checkboxState,
          ...getSelectionsAnalyticsAttributes(contentId),
        })
        .catch(() => undefined);
    });
  }

  function emitKeyboard(event: Extract<SelectionUiInteraction, { source: 'keyboard' }>) {
    const { actionFamily, target } = event.detail;

    if (actionFamily === 'extendRange' || actionFamily === 'extendRangeToEdge') {
      return;
    }

    const entryId = databaseAdapter.getVisibleEntryIdAt(target.row.index);
    const fieldId = databaseAdapter.getVisibleFieldIdAt(target.col.index);

    if (!entryId || !fieldId) {
      return;
    }

    const tableRoot = getTableRoot();
    const metadata = {
      contentId,
      inputSource: 'keyboard' as const,
      keyboardActionFamily: actionFamily,
    };

    switch (actionFamily) {
      case 'moveCell':
      case 'jumpToEdge':
      case 'tabForward':
      case 'tabBackward':
        fireMetric(
          cellSelectionMetric,
          {
            startTime: event.startTime,
            probeData: {
              tableRoot,
              selectionProbe: 'focusBox',
              entryId,
              fieldId,
            },
            metadata: {
              ...metadata,
              selectionProbe: 'focusBox',
            },
            ...(!tableRoot && {
              failImmediately: {
                reason: 'cell-selection-dom-sync-missing-table-root',
              },
            }),
          },
          cellSelectionMetricRef,
        );
        return;

      case 'startEditWithEnter':
      case 'startEditWithTyping':
        fireMetric(
          cellInlineEditMetric,
          {
            startTime: event.startTime,
            probeData: { tableRoot, entryId, fieldId },
            metadata,
            ...(!tableRoot && {
              failImmediately: {
                reason: 'cell-inline-edit-dom-sync-missing-table-root',
              },
            }),
          },
          cellInlineEditMetricRef,
        );
        return;

      default: {
        const unhandledActionFamily: never = actionFamily;
        return unhandledActionFamily;
      }
    }
  }

  return {
    emit(event: SelectionUiInteraction) {
      cancelAllInFlight();

      if (event.source === 'keyboard') {
        emitKeyboard(event);

        return;
      }

      if (event.source === 'cellPointer') {
        emitCellPointer(event);

        return;
      }

      if (event.source === 'rowCheckbox') {
        emitRowCheckbox(event);

        return;
      }

      if (event.source === 'contextMenu') {
        emitContextMenu(event);

        return;
      }

      emitHeaderCheckbox(event);
    },

    cancelInFlightMetrics() {
      cancelAllInFlight();
    },

    dispose() {
      cancelAllInFlight();
      cachedLayoutTable = undefined;
    },
  };
}
